import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import {
  DeadlinesData,
  DeadlinesFilter,
  DeadlinesLastOpenedAtSubject,
  DeadlinesResult,
  DeadlinesTimeline,
  DeadlinesTimelineItem,
  DeadlinesToMaintenancePagesParams
} from '@core/interfaces';
import { fuseAnimations } from '@fuse/animations';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { merge, Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil } from 'rxjs/operators';
import { FilterComponent } from './filter/filter.component';
import { DeadlinesMapperService } from './services/deadlines.mapper.service';
import { DeadlinesResolverService } from './services/deadlines.resolver.service';

import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';

@Component({
  selector: 'deadlines',
  templateUrl: './deadlines.component.html',
  styleUrls: ['./deadlines.component.scss'],
  animations: [fuseAnimations],
})
export class DeadlinesComponent implements OnInit, OnDestroy {
  public isLoading: boolean;
  public isFiltered: boolean;
  public isLoadingOnlyResponseChart: boolean;
  public isLoadingOnlySolutionChart: boolean;

  public data: DeadlinesData;
  public responseTimeline: DeadlinesTimelineItem[];
  public solutionTimeline: DeadlinesTimelineItem[];
  public lastOpenedAtResponse: string;
  public lastOpenedAtSolution: string;

  private intervalID: NodeJS.Timer;
  private autoReloadTime = 2 * 60000;

  private subscriptions = new Subject<void>();
  private refreshData = new Subject<null>();
  public lastOpenedAtChartChanges = new Subject<DeadlinesLastOpenedAtSubject>();

  @ViewChild(FilterComponent, { static: true })
  public filter: FilterComponent;

  constructor(
    private fuseTranslationLoaderService: FuseTranslationLoaderService,
    private fuseProgressBarService: FuseProgressBarService,
    private resolverService: DeadlinesResolverService,
    private mapperService: DeadlinesMapperService,
    private router: Router,
    private location: Location
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
  }

  public ngOnInit(): void {
    this.isLoading = true;
    this.isFiltered = true;
    this.lastOpenedAtResponse = 'lastSevenDays';
    this.lastOpenedAtSolution = 'lastSevenDays';

    merge(
      this.refreshData,
      this.filter.filtersChanged,
      this.lastOpenedAtChartChanges
    )
      .pipe(
        takeUntil(this.subscriptions),
        debounceTime(200),
        switchMap(async (subjectValue: DeadlinesFilter | DeadlinesLastOpenedAtSubject | null) => {
          const { value: filter } = this.filter.filtersChanged;
          const filterIsChanged = subjectValue && JSON.stringify(subjectValue) === JSON.stringify(filter);

          if (!subjectValue || filterIsChanged) {
            this.isFiltered = filter['isFiltered'];
            this.showLoading();

            const data = await this.resolverService.getAverageDeadlinesAndTimeline({
              currentWhere: filter['currentWhere'],
              footerWhere: filter['footerWhere'],
              responsePeriod: this.lastOpenedAtResponse,
              solutionPeriod: this.lastOpenedAtSolution
            });

            return {
              type: 'all',
              data: data
            };
          }

          this.isLoadingOnlyResponseChart = subjectValue['type'] === 'response';
          this.isLoadingOnlySolutionChart = subjectValue['type'] === 'solution';

          const timelineData = await this.resolverService.getTimeline({
            where: filter.currentWhere,
            timelineOption: subjectValue['value']
          });

          return {
            type: subjectValue['type'],
            data: timelineData
          };
        })
      )
      .subscribe((result: DeadlinesResult) => {
        this.hideLoading();
        this.isLoadingOnlyResponseChart = false;
        this.isLoadingOnlySolutionChart = false;

        if (result.type === 'all') {
          const [averageDeadlines, responseTimeline, solutionTimeline] = result.data;

          this.data = averageDeadlines;
          this.toResponseTimeline(responseTimeline);
          this.toSolutionTimeline(solutionTimeline || responseTimeline);

          return;
        }

        return result.type === 'response'
          ? this.toResponseTimeline(result.data)
          : this.toSolutionTimeline(result.data);
      });
  }

  public ngOnDestroy(): void {
    this.subscriptions.next();
    this.subscriptions.complete();
    clearInterval(this.intervalID);
  }

  public autoRefresh(event: { checked: boolean }): void {
    if (event.checked) {
      this.intervalID = setInterval(() => this.refresh(), this.autoReloadTime);
      return;
    }

    clearInterval(this.intervalID);
  }

  public refresh(): void {
    this.refreshData.next();
  }

  public clearFilters(): void {
    this.filter.clearFilters();
  }

  public goToMaintenancesPage(params: DeadlinesToMaintenancePagesParams): void {
    const queryParams = this.mapperService.toMaintenancesQueryParams({
      where: this.filter.filtersChanged.value.currentWhere,
      formValue: this.filter.filterForm.value
    });

    const url = this.router.serializeUrl(this.router.createUrlTree(['manutencoes'], { queryParams: { ...queryParams, ...params } }));
    window.open(this.location.prepareExternalUrl(url), '_blank');
  }

  private showLoading(): void {
    this.isLoading = true;
    this.fuseProgressBarService.show();
  }

  private hideLoading(): void {
    this.isLoading = false;
    this.fuseProgressBarService.hide();
  }

  private toResponseTimeline(responseTimeline: DeadlinesTimeline[]): void {
    this.responseTimeline = this.mapperService.toTimeline({
      data: responseTimeline,
      type: 'firstVisit',
      timelineOption: this.lastOpenedAtResponse
    });
  }

  private toSolutionTimeline(solutionTimeline: DeadlinesTimeline[]): void {
    this.solutionTimeline = this.mapperService.toTimeline({
      data: solutionTimeline,
      type: 'closed',
      timelineOption: this.lastOpenedAtSolution
    });
  }
}
